import {
    FC,
    ReactElement,
    ReactNode,
    UIEventHandler,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import cn from 'classnames';
import IconClose from 'assets/img/close-modal-icon.svg?react';
import Arrow from 'assets/img/arrow.svg?react';
import { Button } from '../core/button';
import './modal.styl';
import { Portal } from './portal';

export interface ModalProps {
    isOpened: boolean;
    children?: ReactNode;
    onClose: () => void;
    title?: string;
    description?: string | ReactElement;
    view?: 'present' | 'prize';
    withArrowBack?: boolean;
    primaryButtonText?: string;
    secondaryButtonText?: string;
    onClickPrimaryBtn?: () => void;
    onClickSecondaryBtn?: () => void;
    primaryButtonDisabled?: boolean;
    secondaryButtonDisabled?: boolean;
    className?: string;
    preopenId?: string;
    onPreopenOpened?: () => void;
    scrollBlurEnabled?: boolean;
    prizeParams?: {
        background: string;
        imgSrc?: string;
    };
}

export const Modal: FC<ModalProps> = ({
    isOpened,
    title,
    description,
    children,
    view = 'present',
    primaryButtonText,
    secondaryButtonText,
    withArrowBack,
    onClose,
    onClickPrimaryBtn,
    onClickSecondaryBtn,
    primaryButtonDisabled,
    secondaryButtonDisabled,
    className,
    scrollBlurEnabled = true,
    prizeParams
}) => {
    const modalLayerRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const [isTopBlurVisible, setIsTopBlurVisible] = useState(false);
    const [isBottomBlurVisible, setIsBottomBlurVisible] = useState(false);
    const [scrollerRef, setScrollerRef] = useState<HTMLDivElement | null>();

    const checkBlurState = useCallback(() => {
        if (!scrollerRef || !scrollBlurEnabled) return;
        const { scrollTop, scrollHeight, offsetHeight } = scrollerRef;
        setIsTopBlurVisible(scrollTop > 2);
        setIsBottomBlurVisible(Math.abs(scrollHeight - offsetHeight - scrollTop) > 2);
    }, [scrollerRef, scrollBlurEnabled]);

    useLayoutEffect(() => {
        checkBlurState();
    }, [checkBlurState]);

    const onScroll: UIEventHandler<HTMLDivElement> = useCallback(() => {
        checkBlurState();
    }, [checkBlurState]);

    useEffect(() => {
        const onModalLayerClick = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as HTMLElement)) {
                e.stopPropagation();
                onClose();
            }
        };

        if (isOpened) {
            modalLayerRef.current?.addEventListener('click', onModalLayerClick);
        }

        return () => {
            modalLayerRef.current?.removeEventListener('click', onModalLayerClick);
        };
    }, [onClose]);

    const handlePrimaryBtnClick = () => {
        onClickPrimaryBtn?.();
    };

    const handleSecondaryBtnClick = () => {
        onClickSecondaryBtn?.();
    };

    const iconCloseElement = !withArrowBack && (
        <div
            className="modal-close-icon"
            onClick={onClose}
            style={{ position: view === 'prize' ? 'absolute' : 'static' }}
        >
            <IconClose className="modal-close-icon-ico" />
        </div>
    );

    const controlsElement =
        primaryButtonText || secondaryButtonText ? (
            <div className="modal-controls">
                {primaryButtonText && (
                    <Button
                        className="orange modal-primary-btn"
                        onClick={handlePrimaryBtnClick}
                        disabled={primaryButtonDisabled}
                    >
                        {primaryButtonText}
                    </Button>
                )}
                {secondaryButtonText && (
                    <Button className="secondary" onClick={handleSecondaryBtnClick} disabled={secondaryButtonDisabled}>
                        {secondaryButtonText}
                    </Button>
                )}
            </div>
        ) : null;

    const prizeViewElement = (
        <div className={cn('modal', 'prize-view-modal', className)} ref={modalRef}>
            {iconCloseElement}
            <div className="modal-prize-header" style={{ background: prizeParams?.background }}>
                {prizeParams?.imgSrc ? (
                    <img className="modal-prize-header-logo" src={prizeParams.imgSrc} alt="logo" />
                ) : null}
            </div>
            <div className="modal-content-wrapper">
                <div className={cn('modal-content', 'prize-view-content')} ref={setScrollerRef} onScroll={onScroll}>
                    {children}
                </div>
                <div className="modal-content-top-blur" style={{ opacity: isTopBlurVisible ? 1 : 0 }} />
                <div className="modal-content-bottom-blur" style={{ opacity: isBottomBlurVisible ? 1 : 0 }} />
            </div>
            {controlsElement}
        </div>
    );

    const presentViewElement = (
        <div className={cn('modal', className)} ref={modalRef}>
            <div className="modal-header">
                <div className="modal-top">
                    {withArrowBack && <Arrow className="modal-arrow-back" onClick={onClose} />}
                    <span className="modal-title">{title}</span>
                    {iconCloseElement}
                </div>

                <span className="modal-description">{description}</span>
            </div>
            <div className="modal-content-wrapper">
                <div className="modal-content" ref={setScrollerRef} onScroll={onScroll}>
                    {children}
                </div>
                <div className="modal-content-top-blur" style={{ opacity: isTopBlurVisible ? 1 : 0 }} />
                {/* <div className="modal-content-bottom-blur" style={{ opacity: isBottomBlurVisible ? 1 : 0 }} /> */}
            </div>
            {controlsElement}
        </div>
    );

    return (
        <>
            {isOpened ? (
                <Portal>
                    <div className="modal-layer" ref={modalLayerRef}>
                        {view === 'prize' && prizeViewElement}
                        {view === 'present' && presentViewElement}
                    </div>
                </Portal>
            ) : null}
        </>
    );
};
